import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import classNames from 'clsx';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useSelector } from 'react-redux';
import { interval } from 'rxjs';

import { CasinoGameTile } from '@swf/casino';
import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import { select } from '@swf/core/store';
import type { CasinoGame, LiveCasinoGame } from '@swf/core/types/CasinoGame';
import { isLiveCasinoGame } from '@swf/core/utils/casino';

import CombinedPerson from '../../assets/combined-person.svg';
import LiveCasinoGameStatus, { overlapAbove } from '../LiveCasinoGameStatus/LiveCasinoGameStatus';
import { GameTypes, getType, hasStatus } from '../LiveCasinoGameStatus/MetaMessage';

import FlagIcon from './FlagIcon';
import styles from './LiveCasinoGameTile.scss';

export interface Props {
  className?: string;
  game: CasinoGame;
  grid: boolean;
  onLaunch?(game: CasinoGame): void;
  favouriteIcon?: JSX.Element;
}

const PlayerCount: React.FC<{ game: LiveCasinoGame }> = ({ game }) => {
  const [{ maxSeats = 0 }] = game.tables;
  const { numPlayers = 0 } = game.statusMeta;
  return (
    <div
      className={classNames(styles.playerSeats, {
        [styles.availableSeats]: numPlayers < maxSeats
      })}
    >
      <CombinedPerson />
      <span>
        {numPlayers}
        {maxSeats > 0 ? ` / ${maxSeats}` : ''}
      </span>
    </div>
  );
};

const getMultiplier = (game: LiveCasinoGame) => game?.statusMeta?.multiplier;

const CasinoStub: React.FC<{
  game: CasinoGame;
  onLaunch?(game: CasinoGame): void;
  favouriteIcon?: JSX.Element;
}> = ({ game, favouriteIcon, onLaunch }) => (
  <>
    <div className={styles.stakeRange} />
    <CasinoGameTile
      className={styles.gameTile}
      jackpotClassName={styles.jackpotRibbon}
      game={{ ...game, tables: game.tables }}
      icon="icnLive"
      onLaunch={onLaunch}
      favouriteIcon={favouriteIcon}
    />
    <div className={styles.name}>
      <span>{game.name}</span>
    </div>
  </>
);

const iconUpdateInterval = 30000;

const LiveStub: React.FC<{
  game: LiveCasinoGame;
  favouriteIcon?: JSX.Element;
  onLaunch?(game: CasinoGame): void;
}> = ({ game, onLaunch, favouriteIcon }) => {
  const multiplier = game ? getMultiplier(game as LiveCasinoGame) : 0;
  const locale = useSelector(select.locale.get);
  const [{ minBet, maxBet }] = game.tables;
  const [needRefresh, setNeedRefresh] = useState(false);
  const [iconVersion, setIconVersion] = useState(1);
  const [activeIcon, setActiveIcon] = useState(game.images.icnLive);
  const isRoulette = getType(game) === GameTypes.Roulette;
  const rouletteStatusEnabled = useFeature(FeatureFlag.CasinoLiveRoulette) && getType(game);

  const { open, openingDateTime } = game.statusMeta;

  const testIcn = get(game, ['statusMeta', 'videoSnapshot', 'thumbnails', 'XL'], '');

  const nextIcon = `${testIcn}?${iconVersion + 1}`;
  const withStatus = hasStatus(game) && ((isRoulette && rouletteStatusEnabled) || !isRoulette);

  useEffect(() => {
    const intv = interval(iconUpdateInterval).subscribe(() => setNeedRefresh(true));

    if (testIcn) {
      const img = new Image();
      img.onload = () => setActiveIcon(img.src);
      img.src = `${testIcn}?${iconVersion}`;
    }

    return () => intv.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (needRefresh) {
      setNeedRefresh(false);
      const img = new Image();
      img.onload = () => {
        setActiveIcon(nextIcon);
        setIconVersion(iconVersion + 1);
      };
      img.src = nextIcon;
    }
  }, [needRefresh, iconVersion, nextIcon]);

  return (
    <>
      {' '}
      <div className={styles.stakeRange}>
        {game && <CurrencyAmount value={minBet} />} -{' '}
        {game && <FormattedNumber value={maxBet / 100} />}
      </div>
      <PlayerCount game={game} />
      <CasinoGameTile
        className={styles.gameTile}
        jackpotClassName={withStatus ? styles.jackpotRibbon : ''}
        game={{ ...game, images: { ...game.images, icnLive: activeIcon } }}
        icon="icnLive"
        onLaunch={onLaunch}
        favouriteIcon={favouriteIcon}
      />
      {withStatus ? (
        <LiveCasinoGameStatus
          game={game}
          className={classNames(styles.specialStatus, {
            [styles.padStatus]: overlapAbove(game)
          })}
        />
      ) : null}
      {!open ? (
        <div className={styles.openSplash}>
          <div className={styles.timeBlock}>
            <div className={styles.openMessage}>Table opens at</div>
            <div className={styles.clock} suppressHydrationWarning>
              {openingDateTime &&
                new Date(openingDateTime).toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
            </div>
          </div>
        </div>
      ) : null}
      <div className={styles.name}>
        <FlagIcon game={game} />
        <span>{game.name}</span>
      </div>
      {multiplier && <div className={styles.multiplier}>X{multiplier}</div>}
    </>
  );
};

const LiveCasinoGameTile: React.FC<Props> = ({ game, favouriteIcon, grid = false, onLaunch }) => {
  const castGame = isLiveCasinoGame(game) ? (game as LiveCasinoGame) : null;

  return (
    <div
      className={classNames(styles.container, {
        [styles.opens]: !castGame?.statusMeta.open,
        [styles.vip]: game.tags?.includes('VIP Table'),
        [styles.grid]: grid
      })}
    >
      {castGame ? (
        <LiveStub game={castGame} onLaunch={onLaunch} favouriteIcon={favouriteIcon} />
      ) : (
        <CasinoStub game={game as CasinoGame} onLaunch={onLaunch} favouriteIcon={favouriteIcon} />
      )}
    </div>
  );
};

export default LiveCasinoGameTile;
